import React, { useState, useEffect } from 'react';
import api from '../api';

const emptyForm = {
  name: '',
  cost: '',
  depreciation_method: 'straight_line',
  useful_life_years: '',
  purchase_date: '',
  residual_value: '0',
};

const FixedAssets = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/fixed-assets/')
      .then(res => setRows(Array.isArray(res.data) ? res.data : res.data.results || []))
      .catch(() => setError('Failed to load fixed assets'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/fixed-assets/', {
        name: form.name,
        cost: Number(form.cost),
        depreciation_method: form.depreciation_method,
        useful_life_years: Number(form.useful_life_years),
        purchase_date: form.purchase_date,
        residual_value: Number(form.residual_value || 0),
      });
      setShowModal(false);
      setForm(emptyForm);
      load();
    } catch {
      setError('Failed to create fixed asset');
    } finally {
      setSaving(false);
    }
  };

  const depreciate = async (id) => {
    const period = window.prompt('Depreciation period (YYYY-MM)', new Date().toISOString().slice(0, 7));
    if (!period) return;
    setBusyId(id);
    setError('');
    try {
      await api.post(`/fixed-assets/${id}/depreciate/`, { period });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Depreciation failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="flex-between">
        <h2>Fixed Assets</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>New Asset</button>
      </div>
      {error && <div className="error">{error}</div>}
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Cost</th>
            <th>Method</th>
            <th>Useful Life</th>
            <th>Purchase Date</th>
            <th>Residual</th>
            <th>Accum. Dep.</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td>{r.name}</td>
              <td>{Number(r.cost).toLocaleString()}</td>
              <td>{r.depreciation_method}</td>
              <td>{r.useful_life_years}</td>
              <td>{r.purchase_date}</td>
              <td>{Number(r.residual_value).toLocaleString()}</td>
              <td>{Number(r.accumulated_depreciation).toLocaleString()}</td>
              <td>
                <button className="btn btn-success" onClick={() => depreciate(r.id)} disabled={busyId === r.id}>
                  {busyId === r.id ? '...' : 'Depreciate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Create Fixed Asset</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Cost</label>
                  <input type="number" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Residual Value</label>
                  <input type="number" step="0.01" value={form.residual_value} onChange={e => setForm({ ...form, residual_value: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Depreciation Method</label>
                  <select value={form.depreciation_method} onChange={e => setForm({ ...form, depreciation_method: e.target.value })}>
                    <option value="straight_line">Straight Line</option>
                    <option value="reducing_balance">Reducing Balance</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Useful Life (Years)</label>
                  <input type="number" min="1" value={form.useful_life_years} onChange={e => setForm({ ...form, useful_life_years: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>Purchase Date</label>
                <input type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} required />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FixedAssets;
